import { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../backend/convex/_generated/api';
import { Id } from '../../../backend/convex/_generated/dataModel';
import { useSearchParams } from 'react-router-dom';
import type { PreviewData, PublishResult } from '@/types';
import { useAuth } from '@/context/use-auth';

interface UseBookEditorReturn {
  // Form state
  markdown: string;
  title: string;
  author: string;
  blurb: string;
  coverUrl: string;
  heroCoverUrl: string;
  tokenCosts: Record<string, number>;
  
  // UI state
  preview: PreviewData | null;
  isPublishing: boolean;
  publishResult: PublishResult | null;
  error: string | null;
  isLoadingBook: boolean;
  hasUnsavedChanges: boolean;
  originalBookId: string | null;
  isEditMode: boolean;
  
  // Actions
  setMarkdown: (value: string) => void;
  setTitle: (value: string) => void;
  setAuthor: (value: string) => void;
  setBlurb: (value: string) => void;
  setCoverUrl: (value: string) => void;
  setHeroCoverUrl: (value: string) => void;
  setTokenCosts: (value: Record<string, number>) => void;
  updateChapterTokenCost: (sequence: number, cost: number) => void;
  setPreview: (value: PreviewData | null) => void;
  handlePublish: () => Promise<void>;
  loadFromFile: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  canPublish: boolean;
}

export function useBookEditor(): UseBookEditorReturn {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const editBookId = searchParams.get('edit');
  const isEditMode = !!editBookId;
  
  // Form state
  const [markdown, setMarkdown] = useState('');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [blurb, setBlurb] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [heroCoverUrl, setHeroCoverUrl] = useState('');
  const [tokenCosts, setTokenCosts] = useState<Record<string, number>>({});
  const [originalBookId, setOriginalBookId] = useState<string | null>(null);
  
  // UI state
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<PublishResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingBook, setIsLoadingBook] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Convex mutations & queries
  const previewMutation = useMutation(api.publisher.preview);
  const publishBook = useMutation(api.publisher.publishBook);
  const bookForEditing = useQuery(
    api.admin.getBookForEditing,
    editBookId && user ? { bookId: editBookId as Id<'books'>, clerkId: user.id } : 'skip'
  );

  // Load book for editing
  useEffect(() => {
    if (bookForEditing && isEditMode) {
      setIsLoadingBook(true);
      setTitle(bookForEditing.book.title);
      setAuthor(bookForEditing.book.author);
      setBlurb(bookForEditing.book.blurb || '');
      setCoverUrl(bookForEditing.book.coverUrl || '');
      setHeroCoverUrl(bookForEditing.book.heroCoverUrl || '');
      setTokenCosts(bookForEditing.book.tokenCosts || {});
      setMarkdown(bookForEditing.markdown);
      setOriginalBookId(bookForEditing.book._id);
      setHasUnsavedChanges(false);
      setIsLoadingBook(false);
    }
  }, [bookForEditing, isEditMode]);

  // Generate preview when book data is loaded
  useEffect(() => {
    if (bookForEditing && isEditMode && markdown.trim().length > 0 && !preview) {
      const generatePreview = async () => {
        try {
          const result = await previewMutation({ markdown, tokenCosts });
          setPreview(result);
        } catch (err) {
          console.error('Preview error:', err);
        }
      };
      generatePreview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookForEditing, isEditMode]);

  // Track unsaved changes
  useEffect(() => {
    if (isEditMode && originalBookId) {
      setHasUnsavedChanges(true);
    }
  }, [title, author, blurb, coverUrl, heroCoverUrl, tokenCosts, markdown, isEditMode, originalBookId]);

  // Warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Auto-preview
  useEffect(() => {
    const timer = setTimeout(() => {
      if (markdown.trim().length > 100) {
        handlePreview();
      }
    }, 600);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markdown, tokenCosts]);

  const handlePreview = useCallback(async () => {
    if (!markdown.trim()) return;
    try {
      const result = await previewMutation({ markdown, tokenCosts });
      setPreview(result);
      setError(null);
    } catch (err) { 
      console.error(err); 
    }
  }, [markdown, tokenCosts, previewMutation]);

  const updateChapterTokenCost = useCallback((sequence: number, cost: number) => {
    setTokenCosts(prev => ({
      ...prev,
      [sequence.toString()]: cost
    }));
  }, []);

  const handlePublish = useCallback(async () => {
    if (!title.trim() || !author.trim() || !markdown.trim()) {
      setError('Title, author, and content are required');
      return;
    }
    setIsPublishing(true);
    setError(null);
    try {
      const result = await publishBook({
        title: title.trim(),
        author: author.trim(),
        blurb: blurb.trim(),
        coverUrl: coverUrl.trim(),
        heroCoverUrl: heroCoverUrl.trim(),
        markdown: markdown.trim(),
        tokenCosts,
      });
      setPublishResult(result);
      setPreview(null);
      setHasUnsavedChanges(false);
      
      if (isEditMode) {
        if (result.bookId !== originalBookId) {
          setOriginalBookId(result.bookId);
        }
      } else if (!result.isUpdate) {
        setMarkdown('');
        setTitle('');
        setAuthor('');
        setBlurb('');
        setCoverUrl('');
        setHeroCoverUrl('');
        setTokenCosts({});
        setOriginalBookId(null);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to publish';
      setError(message);
    } finally {
      setIsPublishing(false);
    }
  }, [title, author, blurb, coverUrl, heroCoverUrl, markdown, tokenCosts, isEditMode, originalBookId, publishBook]);

  const loadFromFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setMarkdown(text);
    const firstLine = text.split('\n')[0].trim();
    if (firstLine.startsWith('# ') && !title) {
      setTitle(firstLine.replace('# ', ''));
    }
  }, [title]);

  const canPublish = !!(title.trim() && author.trim() && markdown.trim());

  return {
    markdown,
    title,
    author,
    blurb,
    coverUrl,
    heroCoverUrl,
    tokenCosts,
    preview,
    isPublishing,
    publishResult,
    error,
    isLoadingBook,
    hasUnsavedChanges,
    originalBookId,
    isEditMode,
    setMarkdown,
    setTitle,
    setAuthor,
    setBlurb,
    setCoverUrl,
    setHeroCoverUrl,
    setTokenCosts,
    updateChapterTokenCost,
    setPreview,
    handlePublish,
    loadFromFile,
    canPublish,
  };
}
